import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Sparkles, MapPin, Quote, Users, Home as HomeIcon } from "lucide-react";
import { ASSETS, TIERS } from "../lib/brand";
import PuzzleHouseHero from "../components/PuzzleHouseHero";
import PuzzleSectionDivider from "../components/PuzzleSectionDivider";
import FloatingPuzzlePieces from "../components/FloatingPuzzlePieces";

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* ---------------- COMMUNITY MOSAIC HERO ---------------- */}
      <section className="relative overflow-hidden">
        <FloatingPuzzlePieces />
        <div className="absolute inset-0 puzzle-dots opacity-30" aria-hidden />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-14 md:pt-20 pb-24 relative">
          <div className="text-center max-w-3xl mx-auto fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-black/5 px-4 py-1.5 text-xs font-medium text-[#4B5563]">
              <Sparkles className="w-3.5 h-3.5 text-[#FFB300]" strokeWidth={1.8} />
              A non-profit rooted in Marion, Arkansas
            </div>
            <h1 className="mt-6 font-heading font-bold tracking-tighter text-[#1F2937] leading-[0.95]
                           text-5xl sm:text-6xl lg:text-7xl text-balance">
              Different, not less.<br />
              <span className="brush-underline brush-blue">Help build our home.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#4B5563] leading-relaxed max-w-2xl mx-auto">
              We're building a permanent, sensory-friendly home for Autism families in Marion — one puzzle piece at a time.
            </p>
          </div>

          {/* House mosaic */}
          <div className="mt-10 max-w-3xl mx-auto fade-up" style={{ animationDelay: "120ms" }}>
            <PuzzleHouseHero progress={0.72} totalGoal={8400} />
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/donate"
              data-testid="hero-donate-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#1F2937] hover:bg-[#111827] text-white px-7 py-3.5 text-sm font-semibold shadow-sm hover:-translate-y-0.5 hover:shadow-lg"
            >
              <Heart className="w-4 h-4" strokeWidth={2} /> Donate a Piece
            </Link>
            <Link
              to="/community"
              data-testid="hero-community-btn"
              className="inline-flex items-center gap-2 rounded-full border border-[#1F2937]/15 hover:border-[#1F2937]/40 bg-white px-7 py-3.5 text-sm font-semibold text-[#1F2937]"
            >
              <Users className="w-4 h-4" /> See the wall
            </Link>
          </div>
        </div>
      </section>

      {/* ------ INTERLOCKING STORY SECTIONS ------ */}
      {/* Our Story — sand */}
      <PuzzleSectionDivider fill="#F4F1EA" />
      <section className="bg-[#F4F1EA] paper-grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Our Story</div>
            <h2 className="mt-4 font-heading font-semibold tracking-tight text-[#1F2937] text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
              A community<br />
              <span className="brush-underline brush-yellow">built on love.</span>
            </h2>
            <p className="mt-6 text-[#4B5563] leading-relaxed">
              Stars of Autism began in Marion, Arkansas — where a mother's love for her daughter grew
              into a mission to build support for every family walking this road.
            </p>
            <Link to="/about" data-testid="story-read-more" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white border border-black/10 hover:border-black/30 px-5 py-2.5 text-sm font-semibold text-[#1F2937]">
              Read our story <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
            {[
              { c: "#2196F3", t: "Education", d: "Workshops & resources for caregivers." },
              { c: "#E53935", t: "Advocacy", d: "Fighting for every family, every voice." },
              { c: "#4CAF50", t: "Community", d: "Belonging in Marion and beyond." },
            ].map((b) => (
              <div key={b.t} className="rounded-2xl bg-white border border-black/5 p-6">
                <div className="w-9 h-9 rounded-full" style={{ backgroundColor: `${b.c}22`, border: `1px solid ${b.c}55` }} />
                <div className="mt-4 font-heading font-semibold text-[#1F2937]">{b.t}</div>
                <div className="text-sm text-[#6B7280] mt-1 leading-relaxed">{b.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interlocking transition: dark section pushes UP into the sand section */}
      <div className="relative">
        <PuzzleSectionDivider fill="#1F2937" />
      </div>

      {/* Future Plans — dark */}
      <section className="bg-[#1F2937] text-white relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-96 h-96 rounded-full bg-[#2196F3]/20 blur-3xl" aria-hidden />
        <div className="absolute -left-16 -bottom-16 w-96 h-96 rounded-full bg-[#FFB300]/15 blur-3xl" aria-hidden />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.2em] text-[#FFB300] font-semibold">Future Plans</div>
            <h2 className="mt-4 font-heading font-semibold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
              A joyful, sensory-friendly<br />
              <span className="brush-underline brush-red">event hall for every child.</span>
            </h2>
            <p className="mt-6 text-white/80 leading-relaxed max-w-lg">
              Our dream includes a safe, welcoming space designed specifically for children with Autism —
              a place to host parties and community events where every child can celebrate, connect,
              and just be themselves.
            </p>
            <Link
              to="/programs"
              data-testid="future-plans-btn"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-[#1F2937] hover:bg-[#FFB300] px-6 py-3 text-sm font-semibold"
            >
              See the vision <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-3xl overflow-hidden border border-white/10 relative aspect-[4/3]">
              <img src={ASSETS.pavilion} alt="Community gathering" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-center gap-3 text-sm">
                <HomeIcon className="w-4 h-4 text-[#FFB300]" strokeWidth={1.6} />
                A home for every family in Marion, Arkansas
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sand back with puzzle transition */}
      <PuzzleSectionDivider fill="#FDFBF7" />

      {/* TIERS */}
      <section className="bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Your Piece Matters</div>
              <h2 className="mt-4 font-heading font-semibold tracking-tight text-[#1F2937] text-3xl sm:text-4xl lg:text-5xl">
                Partnership tiers,<br /> one beautiful puzzle.
              </h2>
            </div>
            <Link to="/donate" data-testid="tiers-see-all-btn"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F2937] hover:text-[#2196F3]">
              See all ways to give <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TIERS.map((t) => (
              <div
                key={t.key}
                data-testid={`home-tier-card-${t.key}`}
                className="relative rounded-3xl bg-white border border-black/5 p-7 overflow-hidden"
                style={{ background: t.bgTint }}
              >
                <div className="tier-stripe" style={{ backgroundColor: t.color }} />
                <div className="text-[0.7rem] tracking-[0.2em] font-semibold uppercase" style={{ color: t.color }}>{t.name.split(" ")[0]}</div>
                <div className="font-heading text-4xl font-bold text-[#1F2937] mt-2">${t.amount}</div>
                <div className="text-sm text-[#6B7280] mt-1">{t.name}</div>
                <div className="mt-5 flex items-start gap-2 text-sm text-[#4B5563] leading-relaxed">
                  <Quote className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.6} style={{ color: t.color }} />
                  <span>"{t.quote}"</span>
                </div>
                <div className="mt-2 text-xs text-[#6B7280]">— {t.author}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-24">
        <div className="rounded-3xl bg-[#F4F1EA] paper-grain p-10 md:p-14 border border-black/5 flex flex-col md:flex-row items-center gap-6 md:justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#E53935]/10 border border-[#E53935]/30 flex items-center justify-center">
              <Heart className="w-5 h-5 text-[#E53935]" strokeWidth={1.6} />
            </div>
            <div>
              <h3 className="font-heading text-2xl font-semibold text-[#1F2937]">Be part of the puzzle.</h3>
              <p className="text-[#6B7280] mt-1">Add your piece to our community wall.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Link to="/community" data-testid="cta-community-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-white border border-black/10 text-[#1F2937] hover:border-black/40 px-6 py-3 text-sm font-semibold">
              <Users className="w-4 h-4" /> Visit the wall
            </Link>
            <Link to="/donate" data-testid="cta-donate-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#2196F3] hover:bg-[#1e88e5] text-white px-6 py-3 text-sm font-semibold">
              <MapPin className="w-4 h-4" /> Donate now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
